// crewbit-admin/src/hooks/useUserPreferences.ts
//
// Mirrors crewbit-mobile/hooks/useUserPreferences.ts.
// Hook contract: docs/references/MONEY_MODULE_SPEC_v1.md §Overview.6 B-5.
// Maps to public.user_preferences (RLS: self-only via get_public_user_id()).
//
// Read-only hook. Per-user (not per-business). Available even when
// requiresBusinessSelection is true so the Settings hub can render
// preferences before a business is picked.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { useAuthStore } from '../stores/authStore';

export type MoneyHeroDefault =
  | 'revenue_month'
  | 'outstanding'
  | 'available_balance'
  | 'net_30d';

export type MoneyPeriodDefault = '7d' | '30d' | 'mtd' | 'qtd' | 'ytd' | 'custom';

export interface UserPreferences {
  moneyHeroDefault: MoneyHeroDefault;
  moneyPeriodDefault: MoneyPeriodDefault;
  notificationPaymentsReceived: boolean;
  notificationDisputesOpened: boolean;
  notificationTaxDue: boolean;
  notificationPayoutsArrived: boolean;
  notificationAccountantInvited: boolean;
  notificationAccountantAvailable: boolean;
  // §Settings Phase 2B additions:
  pushEnabled: boolean;
  notificationScheduleChanges: boolean;
  notificationNewJobAssignments: boolean;
  notificationBookingConfirmations: boolean;
  notificationShiftReminders: boolean;
  notificationLeaveRequestUpdates: boolean;
  notificationTeamAnnouncements: boolean;
  notificationDispatchAlerts: boolean;
}

export interface UseUserPreferencesReturn {
  data: UserPreferences | null;
  isLoading: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

const DEFAULTS: UserPreferences = {
  moneyHeroDefault: 'revenue_month',
  moneyPeriodDefault: '30d',
  notificationPaymentsReceived: true,
  notificationDisputesOpened: true,
  notificationTaxDue: true,
  notificationPayoutsArrived: true,
  notificationAccountantInvited: true,
  notificationAccountantAvailable: true,
  pushEnabled: true,
  notificationScheduleChanges: true,
  notificationNewJobAssignments: true,
  notificationBookingConfirmations: true,
  notificationShiftReminders: true,
  notificationLeaveRequestUpdates: true,
  notificationTeamAnnouncements: true,
  notificationDispatchAlerts: true,
};

interface RawRow {
  money_hero_default: MoneyHeroDefault | null;
  money_period_default: MoneyPeriodDefault | null;
  notification_payments_received: boolean;
  notification_disputes_opened: boolean;
  notification_tax_due: boolean;
  notification_payouts_arrived: boolean;
  notification_accountant_invited: boolean;
  notification_accountant_available: boolean;
  push_enabled: boolean;
  notification_schedule_changes: boolean;
  notification_new_job_assignments: boolean;
  notification_booking_confirmations: boolean;
  notification_shift_reminders: boolean;
  notification_leave_request_updates: boolean;
  notification_team_announcements: boolean;
  notification_dispatch_alerts: boolean;
}

function rowToPrefs(row: RawRow | null): UserPreferences {
  if (!row) return DEFAULTS;
  return {
    moneyHeroDefault: row.money_hero_default ?? DEFAULTS.moneyHeroDefault,
    moneyPeriodDefault: row.money_period_default ?? DEFAULTS.moneyPeriodDefault,
    notificationPaymentsReceived: row.notification_payments_received,
    notificationDisputesOpened: row.notification_disputes_opened,
    notificationTaxDue: row.notification_tax_due,
    notificationPayoutsArrived: row.notification_payouts_arrived,
    notificationAccountantInvited: row.notification_accountant_invited,
    notificationAccountantAvailable: row.notification_accountant_available,
    pushEnabled: row.push_enabled,
    notificationScheduleChanges: row.notification_schedule_changes,
    notificationNewJobAssignments: row.notification_new_job_assignments,
    notificationBookingConfirmations: row.notification_booking_confirmations,
    notificationShiftReminders: row.notification_shift_reminders,
    notificationLeaveRequestUpdates: row.notification_leave_request_updates,
    notificationTeamAnnouncements: row.notification_team_announcements,
    notificationDispatchAlerts: row.notification_dispatch_alerts,
  };
}

export function useUserPreferences(): UseUserPreferencesReturn {
  const publicUserId = useAuthStore((s) => s.user?.id ?? null);

  const [data, setData] = useState<UserPreferences | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const doFetch = useCallback(async () => {
    if (!publicUserId) {
      setData(null);
      return;
    }
    setIsLoading(true);
    try {
      const { data: row, error: queryError } = await supabase
        .from('user_preferences')
        .select(
          'money_hero_default, money_period_default, notification_payments_received, ' +
            'notification_disputes_opened, notification_tax_due, notification_payouts_arrived, ' +
            'notification_accountant_invited, notification_accountant_available, ' +
            'push_enabled, notification_schedule_changes, notification_new_job_assignments, ' +
            'notification_booking_confirmations, notification_shift_reminders, ' +
            'notification_leave_request_updates, notification_team_announcements, ' +
            'notification_dispatch_alerts',
        )
        .eq('user_id', publicUserId)
        .maybeSingle<RawRow>();

      if (!mountedRef.current) return;

      if (queryError) {
        setError(new Error(queryError.message));
        return;
      }

      setData(rowToPrefs(row ?? null));
      setError(null);
    } finally {
      if (mountedRef.current) setIsLoading(false);
    }
  }, [publicUserId]);

  useEffect(() => {
    void doFetch();
  }, [doFetch]);

  return useMemo(
    () => ({ data, isLoading, error, refetch: doFetch }),
    [data, isLoading, error, doFetch],
  );
}
